// Client for the ag-009 tenant-admin surface (/v1/g/{slug}/admin/*).
//
// The GSA admin authenticates per tenant: the bearer token is minted by the
// slug-scoped login endpoint and is only ever sent back to that same slug's
// admin routes. Tokens live in sessionStorage (per-tab, gone on close) — this
// is a low-frequency authoring surface, not a long-lived session product.

import { apiUrl } from '@/lib/apiBase'

// ─── Contract types — mirror src/api/routers/tenants.py (tenant_admin_router) ──

export interface BrownAct {
  ok: boolean
  note: string
}

export interface AdminMeeting {
  meeting_id: string
  meeting_date: string | null
  meeting_type: string | null
  location: string | null
  agenda_url: string | null
  minutes_url: string | null
  video_url: string | null
  brown_act: BrownAct
}

export type AnnouncementStatus = 'draft' | 'published' | 'archived'

export interface AdminAnnouncement {
  id: number
  title: string
  body: string
  status: AnnouncementStatus
  created_at: string | null
  updated_at: string | null
  published_at: string | null
}

export interface MeetingDraft {
  meeting_date: string
  meeting_type: string
  agenda_url?: string | null
  minutes_url?: string | null
  video_url?: string | null
  location?: string | null
}

export const MEETING_TYPES = ['Regular', 'Special', 'Committee'] as const

export class TenantAdminApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'TenantAdminApiError'
    this.status = status
  }
}

// ─── Token storage (sessionStorage, keyed per slug) ────────────────────────────

const tokenKey = (slug: string) => `agrolense.tenant-admin-token.${slug}`

export function getTenantToken(slug: string): string | null {
  try {
    return window.sessionStorage.getItem(tokenKey(slug))
  } catch {
    return null
  }
}

export function clearTenantToken(slug: string): void {
  try {
    window.sessionStorage.removeItem(tokenKey(slug))
  } catch {
    // Storage unavailable (private mode edge cases) — nothing to clear.
  }
}

function storeTenantToken(slug: string, token: string): void {
  window.sessionStorage.setItem(tokenKey(slug), token)
}

// ─── HTTP helpers ──────────────────────────────────────────────────────────────

const adminUrl = (slug: string, path: string) =>
  apiUrl(`/api/agro/v1/g/${encodeURIComponent(slug)}/admin${path}`)

export async function tenantAdminLogin(
  slug: string,
  email: string,
  password: string,
): Promise<void> {
  const res = await fetch(adminUrl(slug, '/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    // The backend intentionally returns a generic 401 (no slug/admin
    // enumeration); mirror that with a generic client message.
    throw new TenantAdminApiError(
      res.status,
      res.status === 429
        ? 'Too many sign-in attempts. Wait a minute and try again.'
        : 'Sign-in failed. Check your email and password.',
    )
  }
  const data = (await res.json()) as { access_token: string }
  storeTenantToken(slug, data.access_token)
}

/** Authenticated JSON request against the tenant's admin surface. */
async function adminFetch<T>(slug: string, path: string, init: RequestInit = {}): Promise<T> {
  const token = getTenantToken(slug)
  if (!token) throw new TenantAdminApiError(401, 'Not signed in.')

  const res = await fetch(adminUrl(slug, path), {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  })

  if (res.status === 401) {
    // Expired/invalid token — drop it so the shell bounces to login.
    clearTenantToken(slug)
    throw new TenantAdminApiError(401, 'Your session expired. Sign in again.')
  }
  if (!res.ok) {
    let detail = ''
    try {
      const body = (await res.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') detail = body.detail
    } catch {
      // Non-JSON error body — fall through to the generic message.
    }
    throw new TenantAdminApiError(res.status, detail || `Request failed (${res.status}).`)
  }
  return (await res.json()) as T
}

// ─── Meetings (screen 3) ───────────────────────────────────────────────────────

export const listAdminMeetings = (slug: string) =>
  adminFetch<AdminMeeting[]>(slug, '/meetings?limit=100')

export const createMeeting = (slug: string, draft: MeetingDraft) =>
  adminFetch<AdminMeeting>(slug, '/meetings', {
    method: 'POST',
    body: JSON.stringify(draft),
  })

// ─── Announcements (screen 4) ──────────────────────────────────────────────────

export const listAdminAnnouncements = (slug: string) =>
  adminFetch<AdminAnnouncement[]>(slug, '/announcements?limit=200')

export const createAnnouncement = (
  slug: string,
  input: { title: string; body: string; publish: boolean },
) =>
  adminFetch<AdminAnnouncement>(slug, '/announcements', {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateAnnouncement = (
  slug: string,
  id: number,
  patch: { title?: string; body?: string },
) =>
  adminFetch<AdminAnnouncement>(slug, `/announcements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const publishAnnouncement = (slug: string, id: number) =>
  adminFetch<AdminAnnouncement>(slug, `/announcements/${id}/publish`, { method: 'POST' })

export const archiveAnnouncement = (slug: string, id: number) =>
  adminFetch<AdminAnnouncement>(slug, `/announcements/${id}/archive`, { method: 'POST' })

// ─── Brown-Act client-side hint (mirrors tenant_meeting_service.brown_act_check)

const BROWN_ACT_LEAD_DAYS: Record<string, number> = { Regular: 3, Special: 1 }

/**
 * Days from today (local calendar day) until an ISO ``YYYY-MM-DD`` date.
 * Returns null when the date is missing/unparseable.
 */
export function daysUntil(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null
  const parsed = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return null
  const now = new Date()
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((parsed.getTime() - todayUtc) / 86_400_000)
}

/**
 * Non-blocking Brown-Act posting warning for the post-a-meeting form.
 * Returns a human warning string, or null when the lead time is satisfied
 * (or the type has no statutory minimum, e.g. Committee).
 */
export function brownActWarning(
  meetingType: string,
  meetingDate: string,
): string | null {
  const lead = BROWN_ACT_LEAD_DAYS[meetingType]
  const days = daysUntil(meetingDate)
  if (lead === undefined || days === null) return null
  if (days >= lead) return null
  return (
    `${meetingType} meetings need the agenda posted at least ${lead * 24} hours ` +
    `before the meeting — this date is ${days} day${days === 1 ? '' : 's'} away. ` +
    'You can still post it (this is a reminder, not a block).'
  )
}
